// src/screens/Home/Home.js

import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  ArrowRight,
  CheckCircle,
  Smartphone,
  Download,
  TrendingUp,
  DollarSign,
  Moon,
  Sun,
} from "lucide-react";
import "./Home.css";

// Importa as imagens
import aboutImage from "../../assets/agro1.jpg";
import app1Image from "../../assets/app1.jpg";
import app2Image from "../../assets/app2.jpg";
import app3Image from "../../assets/app3.jpg";
import app4Image from "../../assets/app4.jpg";
import icon1 from "../../assets/calcleite.png";
import icon2 from "../../assets/calcagro.png";
import icon3 from "../../assets/calccafe.png";
import icon4 from "../../assets/calcbov.png";

export default function Home() {
  const titleRef = useRef(null);

  // Estado do Tema
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("siteTheme") || "light";
  });

  useEffect(() => {
    document.body.className = theme === "dark" ? "dark-mode-body" : "";
    localStorage.setItem("siteTheme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  // Animação do título
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-title");
          }
        });
      },
      { threshold: 0.5 },
    );

    const titleRefCurrent = titleRef.current;
    if (titleRefCurrent) {
      observer.observe(titleRefCurrent);
    }

    return () => {
      if (titleRefCurrent) {
        observer.unobserve(titleRefCurrent);
      }
    };
  }, []);

  return (
    <div className={`app-container ${theme === "dark" ? "dark-mode" : ""}`}>
      {/* Botão Flutuante de Troca de Tema */}
      <button
        onClick={toggleTheme}
        className="theme-toggle-btn"
        aria-label="Alternar Tema"
      >
        {theme === "light" ? <Moon size={24} /> : <Sun size={24} />}
      </button>

      {/* HERO SECTION */}
      <section className="hero-modern-section">
        <div className="hero-blob">
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <path
              className="blob-path"
              d="M44.7,-76.4C58.9,-69.2,71.8,-59.1,81.6,-46.6C91.4,-34.1,98.1,-19.2,95.8,-5.3C93.5,8.6,82.2,21.5,71.1,32.8C60,44.1,49.1,53.8,37.3,61.6C25.5,69.4,12.7,75.3,-1.2,77.4C-15.1,79.5,-29.3,77.8,-42.3,70.7C-55.3,63.6,-67.1,51.1,-75.6,36.7C-84.1,22.3,-89.3,6,-86.4,-9.2C-83.5,-24.4,-72.5,-38.5,-60.3,-49.6C-48.1,-60.7,-34.7,-68.8,-21.3,-72.3C-7.9,-75.8,5.5,-74.7,18.5,-73.6Z"
              transform="translate(100 100)"
            />
          </svg>
        </div>

        <div className="container hero-grid">
          <div className="hero-text-col">
            <div className="badge-new">Nova Versão 2.0 Disponível</div>

            <h1 ref={titleRef} className="hero-modern-title">
              Do Plantio ao Lucro: <br />
              <span className="text-gradient">Controle Total.</span>
            </h1>

            <p className="hero-modern-subtitle">
              Software especializado para produtores de Café, Leite, Grãos e
              Pecuária. Simule cenários, corte custos e aumente sua margem de
              lucro.
            </p>

            <div className="hero-buttons-wrapper">
              <button className="store-button apple-btn">
                <div className="store-icon">
                  <Smartphone size={24} />
                </div>
                <div className="store-text">
                  <span>Baixar no</span>
                  <strong>App Store</strong>
                </div>
              </button>
              <button className="store-button google-btn">
                <div className="store-icon">
                  <Download size={24} />
                </div>
                <div className="store-text">
                  <span>Disponível no</span>
                  <strong>Google Play</strong>
                </div>
              </button>
            </div>

            <div className="hero-trust-indicators">
              <div className="trust-item">
                <CheckCircle size={16} className="trust-icon" /> Teste grátis 7
                dias
              </div>
              <div className="trust-divider">|</div>
              <div className="trust-item">
                <CheckCircle size={16} className="trust-icon" /> Com ou sem
                cartão de crédito
              </div>
            </div>
          </div>

          <div className="hero-img-col">
            <div className="phone-mockup-wrapper">
              <div className="phone-mockup">
                <img
                  src={app1Image}
                  alt="Dashboard App Custos Agro"
                  className="phone-screen-img"
                />
                <div className="phone-overlay-info">
                  <p className="overlay-title">Simulação Safra 2025</p>
                  <p className="overlay-profit">
                    <TrendingUp size={16} /> Lucro estimado: +22%
                  </p>
                </div>
              </div>
              <div className="floating-card">
                <div className="floating-icon-box">
                  <DollarSign size={20} />
                </div>
                <div className="floating-info">
                  <span>Custo da Saca</span>
                  <strong>R$ 840,00</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seção Sobre Nós */}
      <section className="about-section">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2 className="about-title">
                Nós produzimos aplicativos para o seu agronegócio
              </h2>
              <p className="about-paragraph">
                Bem-vindo à Custos Agro, onde oferecemos soluções inovadoras
                para gerenciar os custos da sua produção. Ajudamos produtores a
                otimizar recursos e aumentar a produtividade.
              </p>
              <p className="about-paragraph">
                Com o CustosAgro, você obterá independência e autonomia quando
                se trata das economias e finanças do seu negócio.
              </p>
              <Link to="/sobre" className="about-link">
                Mais sobre a Custos Agro
                <ArrowRight className="about-link-icon" />
              </Link>
            </div>
            <div className="about-image-wrapper">
              <img
                src={aboutImage}
                alt="Agricultor com tablet"
                className="about-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Seção de Aplicativos */}
      <section className="apps-section">
        <div className="container">
          <h2 className="apps-title">Confira nossos aplicativos</h2>
          <p className="apps-subtitle">
            Descubra nossas ferramentas digitais feitas sob medida para o
            agronegócio.
          </p>
          <div className="apps-grid">
            <AppCard
              image={app1Image}
              icon={icon1}
              title="CalcLeite"
              description="Calcula custos e rentabilidade da produção de leite."
              link="/calcleite"
            />
            <AppCard
              image={app3Image}
              icon={icon3}
              title="CalcCafé"
              description="Gerencia a produção de café e calcula custos."
              link="/calccafe"
            />
            <AppCard
              image={app4Image}
              icon={icon4}
              title="CalcBov"
              description="Custos de cria, recria e engorda bovina."
              link="/calcbov"
            />
            <AppCard
              image={app2Image}
              icon={icon2}
              title="CalcAgro"
              description="Calcula custos de culturas agrícolas gerais."
              link="/calcagro"
            />
          </div>
        </div>
      </section>

      {/* Seção de Planos - ATUALIZADA COM NOVOS VALORES E BADGES */}
      <section className="pricing-section">
        <div className="container">
          <h2 className="pricing-title">Escolha o plano ideal</h2>
          <p className="pricing-subtitle">
            Ferramentas para maximizar a lucratividade do seu agronegócio.
          </p>
          <div className="pricing-grid">
            {/* PLANO MENSAL */}
            <PricingCard
              title="Plano Mensal"
              price="R$ 19,90"
              period="cobrado mensalmente"
              // Não tem badge (desconto), mas mostramos flexibilidade
              priceDetail="Flexibilidade total"
              features={[
                "Acesso a 1 aplicativo",
                "Cálculos ilimitados",
                "Cancele quando quiser",
                "Suporte via e-mail",
              ]}
              link="/assinar"
              isFeatured={false}
            />

            {/* PLANO ANUAL (DESTAQUE) */}
            <PricingCard
              title="Plano Anual"
              price="R$ 199,90"
              period="cobrado anualmente"
              // Destaque de 2 meses grátis
              badge="GANHE 7 DIAS GRÁTIS"
              priceDetail="Equivalente a R$ 16,65/mês"
              features={[
                "Acesso a 1 aplicativo",
                "Economia de R$ 39,00/ano", // Economia explícita
                "Suporte Prioritário",
                "Simulações avançadas",
              ]}
              link="/assinar"
              isFeatured={true}
            />

            {/* PLANO TRIMESTRAL */}
            <PricingCard
              title="Plano Trimestral"
              price="R$ 49,90"
              period="cobrado a cada 3 meses"
              // Destaque de economia percentual
              badge="ECONOMIZE 16%"
              priceDetail="Equivalente a R$ 16,63/mês"
              features={[
                "Acesso a 1 aplicativo",
                "Desconto sobre o mensal",
                "Renovação automática",
                "Suporte via WhatsApp",
              ]}
              link="/assinar"
              isFeatured={false}
            />
          </div>
        </div>
      </section>

      {/* Seção CTA */}
      <section className="cta-section">
        <div className="container">
          <h3 className="cta-title">Pronto para transformar sua gestão?</h3>
          <p className="cta-subtitle">Tome decisões inteligentes hoje.</p>
          <Link to="/contatos" className="cta-button-light">
            Fale Conosco
          </Link>
        </div>
      </section>

      {/* Rodapé */}
      <footer className="footer-section">
        <div className="container footer-content">
          <p>
            &copy; {new Date().getFullYear()} Custos Agro |{" "}
            <Link to="/politica-privacidade" className="footer-link">
              Política de Privacidade
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}

// Componentes Auxiliares
const AppCard = ({ image, icon, title, description, link }) => (
  <Link to={link} className="app-card">
    <img src={image} alt={title} className="app-card-image" />
    <div className="app-card-overlay"></div>
    <div className="app-card-content">
      <div className="app-card-header">
        <div className="app-icon-wrapper">
          <img src={icon} alt={`${title} Icon`} className="app-icon" />
        </div>
        <h3 className="app-card-title">{title}</h3>
      </div>
      <p className="app-card-description">{description}</p>
      <div className="app-card-link">
        Confira <ChevronRight className="app-card-link-icon" />
      </div>
    </div>
  </Link>
);

// PricingCard Atualizado com Badge e PriceDetail
const PricingCard = ({
  title,
  price,
  period,
  features,
  link,
  isFeatured,
  badge,
  priceDetail,
}) => (
  <div className={`pricing-card ${isFeatured ? "featured" : ""}`}>
    {/* Renderiza a etiqueta de desconto se existir */}
    {badge && <div className="pricing-badge">{badge}</div>}

    <div className="pricing-card-header">
      <h3 className="pricing-card-title">{title}</h3>
      <p className="pricing-card-price">{price}</p>

      {/* Detalhe do preço (Ex: Equivalente a R$ 16/mês) */}
      {priceDetail && <p className="pricing-card-detail">{priceDetail}</p>}

      <p className="pricing-card-period">{period}</p>
    </div>

    <ul className="pricing-card-features">
      {features.map((feature, index) => (
        <li key={index} className="pricing-card-feature-item">
          <CheckCircle className="pricing-card-check-icon" />
          <span>{feature}</span>
        </li>
      ))}
    </ul>

    <Link to={link} className="pricing-card-button">
      {isFeatured ? "Assinar Agora" : "Assinar"}
    </Link>
  </div>
);
